/* eslint-disable @next/next/no-img-element */
import React from 'react'
import styled from '@emotion/styled'
import { mq } from '../../../helpers'
import { Header1, Text } from '../../../styles/Components'
import Button from '../../Button'
import { gsColors as c } from '../../../styles/Variables'
import HeroImageDesktop from '../../../../public/home/GS_Hero_2026_desktop.jpg'
import HeroImageMobile from '../../../../public/home/GS_Hero_2026_mobile.jpeg'

// Block 1 — full-bleed hero image with a subtle bottom gradient and, overlaid on
// top, a vertically-centered group: the heading with the CTA stacked beneath it.
// Per the updated design the CTA now lives INSIDE the hero (it used to sit in the
// white copy block below). Tier values: desktop = Figma 1440; 1024 = interpolated
// tablet; 768 = Figma 393.
const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1600px;
  height: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${mq('max', '1024px')} {
    height: 480px;
  }

  ${mq('max', '768px')} {
    height: 320px;
  }

  /* Above the design width (1440), the fixed 600px height would make the box wider
     than the crop's 2.4:1 ratio and over-crop the subject vertically. Let height grow
     with width instead so the box keeps the image's aspect (12/5 = 2.4 = 1440/600). */
  ${mq('min', '1440px')} {
    height: auto;
    aspect-ratio: 12 / 5;
  }

  .hero-img {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;

    ${mq('max', '768px')} {
      object-position: center top;
    }
  }

  .gradient {
    position: absolute;
    inset: 0;
    z-index: 1;
    /* Same stops as before, but the updated design lightens the wash (black stop
       0.5 → 0.2 opacity) so the centered heading/CTA read over a brighter image. */
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.2) 39.844%,
      rgba(0, 0, 0, 0) 65.469%
    );
  }

  /* Overlaid logo + heading + CTA group, stacked and centered like the 2023
     video hero (2023/HeroSection.js), then nudged down. translateY scales with
     hero height (~12.5%) so the drop reads the same across breakpoints. */
  .hero-content {
    position: relative;
    z-index: 2;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 0 88px;
    transform: translateY(75px);

    ${mq('max', '1024px')} {
      gap: 16px;
      padding: 0 48px;
      transform: translateY(60px);
    }

    ${mq('max', '768px')} {
      gap: 12px;
      padding: 0 16px;
      transform: translateY(40px);
    }

    /* White GLAMSQUAD wordmark (public/home/new-logo.svg — same asset as the nav
       and 2023 hero). Sized as a share of the hero width to match Figma
       (836/1440 desktop, 301/393 mobile). */
    .hero-logo {
      width: 58%;
      height: auto;

      ${mq('max', '768px')} {
        width: 76%;
      }
    }
  }

  /* Headline uses Header1's own sizes (54px desktop / 28px mobile) — identical to
     the 2023 video hero and the updated Figma — so we only layer on the Figma
     letter-spacing (0.02em). */
  h1 {
    letter-spacing: 1.08px;

    ${mq('max', '768px')} {
      letter-spacing: 0.56px;
    }
  }

  /* CTA sizing/overrides. Color comes from Button's defaults (sunsetOrange bg +
     white text) and the hover prop (which applies tangerineHover) — no local color needed. */
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 304px;
    height: 54px;
    letter-spacing: 1.44px;

    /* Figma uses the SMALL secondary CTA on mobile, not the large one. Reset the
       desktop width/height overrides so the reusable Button renders at its own
       compact mobile size (min-width 200, min-height 48, 14px font). */
    ${mq('max', '768px')} {
      width: auto;
      height: auto;
      letter-spacing: 1.12px;
    }
  }
`

// Block 2 — white block with the mission quote (1px left rule). The CTA that used
// to live here has moved into the hero above, so this block is quote-only now.
const CopyWrapper = styled.div`
  width: 100%;
  max-width: 1600px;
  background-color: ${c.white};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 72px 88px;

  ${mq('max', '1024px')} {
    padding: 48px;
  }

  ${mq('max', '768px')} {
    padding: 28px 16px;
  }

  .quote-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 104px;
    width: 100%;

    ${mq('max', '1024px')} {
      gap: 64px;
    }

    ${mq('max', '768px')} {
      gap: 31px;
    }

    .rule {
      align-self: stretch;
      flex-shrink: 0;
      width: 1px;
      background-color: ${c.black};
    }

    p {
      flex: 1;
      min-width: 0;
      font-size: 38px;
      line-height: 1.38;
      letter-spacing: 0.38px;
      color: ${c.black};

      ${mq('max', '1024px')} {
        font-size: 28px;
        letter-spacing: 0.28px;
      }

      ${mq('max', '768px')} {
        font-size: 18px;
        letter-spacing: 0.18px;
      }
    }
  }
`

const HeroSection = () => (
  <>
    <HeroImageWrapper>
      <picture>
        <source media="(max-width: 768px)" srcSet={HeroImageMobile.src} />
        <img
          className="hero-img"
          src={HeroImageDesktop.src}
          alt="Three women dressed up and smiling on a city rooftop, glam-ready with GLAMSQUAD at-home beauty services"
          fetchPriority="high"
          loading="eager"
          decoding="async"
        />
      </picture>
      <div className="gradient" />
      <div className="hero-content">
        <img className="hero-logo" src="/home/new-logo.svg" alt="GLAMSQUAD" />
        <Header1 white align="center">
          BEAUTY ON CALL
        </Header1>
        <Button large hover path="/book">
          Book Now
        </Button>
      </div>
    </HeroImageWrapper>

    <CopyWrapper>
      <div className="quote-row">
        <div className="rule" />
        <Text variant="large">
          We&apos;re on a mission to eliminate bad hair days, chipped nails, and smokey-eye fails by bringing certified and talented hair stylists, makeup artists, and nail pros right to your door.
        </Text>
      </div>
    </CopyWrapper>
  </>
)

export default HeroSection
